import React, { Component } from 'react';
import { func, oneOfType, string } from 'prop-types';
import { FormattedMessage } from '../../util/reactIntl';
import truncate from 'lodash/truncate';
import classNames from 'classnames';
import { AvatarLarge, InlineTextButton, NamedLink } from '../../components';
import { ensureCurrentUser, ensureUser } from '../../util/data';
import { propTypes } from '../../util/types';

import css from './UserCard.css';

// Approximated collapsed size so that there are ~three lines of text
// in the desktop layout in the host section of the ListingPage.
const BIO_COLLAPSED_LENGTH = 170;

const truncated = s => {
  return truncate(s, {
    length: BIO_COLLAPSED_LENGTH,

    // Allow truncated text end only in specific characters. This will
    // make the truncated text shorter than the length if the original
    // text has to be shortened and the substring ends in a separator.
    //
    // This ensures that the final text doesn't get cut in the middle
    // of a word.
    separator: /\s|,|\.|:|;/,
    omission: '…',
  });
};

class ExpandableBio extends Component {
  constructor(props) {
    super(props);
    this.state = { expand: false };
  }
  render() {
    const { expand } = this.state;
    const { className, bio } = this.props;
    const truncatedBio = truncated(bio);

    const handleShowMoreClick = () => {
      this.setState({ expand: true });
    };
    const showMore = (
      <InlineTextButton rootClassName={css.showMore} onClick={handleShowMoreClick}>
        <FormattedMessage id="UserCard.showFullBioLink" />
      </InlineTextButton>
    );
    return (
      <p className={className}>
        {expand ? bio : truncatedBio}
        {bio !== truncatedBio && !expand ? showMore : null}
      </p>
    );
  }
}

ExpandableBio.defaultProps = { className: null };

ExpandableBio.propTypes = {
  className: string,
  bio: string.isRequired,
};

const UserCard = props => {
  const { rootClassName, className, user, currentUser, onContactUser, onViewProfile, isAllowedToBook = true } = props;

  const userIsCurrentUser = user && user.type === 'currentUser';
  const ensuredUser = userIsCurrentUser ? ensureCurrentUser(user) : ensureUser(user);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const isCurrentUser =
    ensuredUser.id && ensuredCurrentUser.id && ensuredUser.id.uuid === ensuredCurrentUser.id.uuid;
  const { displayName, bio } = ensuredUser.attributes.profile;

  const handleContactUserClick = () => {
    onContactUser(user);
  };

  const hasBio = !!bio;
  const classes = classNames(rootClassName || css.root, className);
  const linkClasses = classNames(css.links, {
    [css.withBioMissingAbove]: !hasBio,
  });

  const separator = isCurrentUser ? null : <span className={css.linkSeparator}>•</span>;

  const { authorIntercomData } = user && user.id && user.attributes || {};

  const getResponseTimeText = averageResponseTime => {
    if (!averageResponseTime || averageResponseTime === 0)
      return 'N/A';
    const convertedSecond = averageResponseTime / 1000;
    const convertedMinutes = convertedSecond / 60;
    const convertedHours = convertedMinutes / 60;
    const convertedDays = convertedHours / 24;
    const textResult = '';
    if (convertedDays > 30) {
      return `${textResult} months`
    }
    if (convertedDays > 7) {
      return `${textResult} within weeks`
    }
    if (convertedDays > 1) {
      return `${textResult} days`
    }
    if (convertedHours >= 1) {
      return `${textResult} ${Math.ceil(convertedHours)} ${convertedHours === 1 ? `hour` : `hours`}`;
    }
    if (convertedMinutes >= 1) {
      return `${textResult} ${Math.ceil(convertedMinutes)} ${convertedMinutes === 1 ? `minute` : `minutes`}`;
    }
    else
      return `${textResult} ${Math.ceil(convertedSecond)} ${convertedSecond === 1 ? `second` : `seconds`}`;
  }

  const intercomData = authorIntercomData && authorIntercomData.attributes && (
    <React.Fragment>
      <p
        className={css.respondedTime}>
        <strong>Response time:</strong> {
          getResponseTimeText(authorIntercomData.attributes.average_booking_responded_time_as_host)
        }
      </p>
      <p
        className={css.respondedTime}>
        <strong>Response rate:</strong> {
          ((authorIntercomData.attributes.number_of_booking_responded_as_host /
            authorIntercomData.attributes.booking_requests_received) * 100).toFixed(0)
        }%
        </p>
      {/* {isCurrentUser && (<p
        className={css.respondedTime}>
        <strong>Booking acceptance rate:</strong> {(authorIntercomData.attributes.accepted_bookings_as_host
          / authorIntercomData.attributes.booking_requests_received).toFixed(4) * 100}%
        </p>)} */}
    </React.Fragment>
  )

  const contact = isAllowedToBook ? (
    <InlineTextButton rootClassName={css.contact} onClick={handleContactUserClick}>
      <FormattedMessage id="UserCard.contactUser" />
    </InlineTextButton>
  ) : null;

  const editProfileMobile = (
    <span className={css.editProfileMobile}>
      <span className={css.linkSeparator}>•</span>
      <NamedLink name="ProfileSettingsPage">
        <FormattedMessage id="ListingPage.editProfileLink" />
      </NamedLink>
    </span>
  );

  const editProfileDesktop = isCurrentUser ? (
    <NamedLink className={css.editProfileDesktop} name="ProfileSettingsPage">
      <FormattedMessage id="ListingPage.editProfileLink" />
    </NamedLink>
  ) : null;

  const links = ensuredUser.id ? (
    <p className={linkClasses}>
      <NamedLink className={css.link} name="ProfilePage" params={{ id: ensuredUser.id.uuid }} onClick={onViewProfile}>
        <FormattedMessage id="UserCard.viewProfileLink" />
      </NamedLink>
      {separator}
      {isCurrentUser ? editProfileMobile : contact}
    </p>
  ) : null;

  return (
    <div className={classes}>
      <div className={css.content}>
        <AvatarLarge className={css.avatar} user={user} />
        <div className={css.info}>
          <div className={css.headingRow}>
            <h3 className={css.heading}>
              <FormattedMessage id="UserCard.heading" values={{ name: displayName }} />
            </h3>
            {editProfileDesktop}
          </div>
          {hasBio ? <ExpandableBio className={css.desktopBio} bio={bio} /> : null}
          {links}
        </div>
      </div>
      {hasBio ? <ExpandableBio className={css.mobileBio} bio={bio} /> : null}
      {intercomData}
    </div>
  );
};

UserCard.defaultProps = {
  rootClassName: null,
  className: null,
  user: null,
  currentUser: null,
};

UserCard.propTypes = {
  rootClassName: string,
  className: string,
  user: oneOfType([propTypes.user, propTypes.currentUser]),
  currentUser: propTypes.currentUser,
  onContactUser: func.isRequired,
  onViewProfile: func,
};

export default UserCard;
